import {Component, HostListener, inject, Input, OnInit} from '@angular/core';
import moment from 'moment';
import {TranslateDirective} from '../../../common/utils/translate.directive';
import {View} from '../../../common/view';
import {ChartDataPerMeasurement} from '../../../utils/chart-data-provider';
import {RefdataUtils} from "../../refdata/refdata-utils";
import {Handler} from "../../../common/handler";
import {BuildingType, DataType, Location} from "@flowmaps-com/flowmaps-typescriptmodels";
import {Entity} from "../../../handlers/entity";
import {
    WeiiCalculationOptionAvailability,
    WeiiCalculationOptionId,
    WeiiCalculationResult,
    WeiiCalculationService
} from "../../../utils/weii-calculation.service";

@Component({
    template: "",
    styleUrls: ['./entity-energy-compass.component.scss']
})
@Handler()
export class EntityEnergyCompassComponent extends View implements OnInit {
    protected weiiCalculationService = inject(WeiiCalculationService);

    refDataUtils = RefdataUtils;
    _location: Location;
    _meters: Entity[];
    buildingType: BuildingType;
    latestMeasurements: ChartDataPerMeasurement;

    mode: 'weii' | 'crrem' = 'weii';
    expanded = false;
    displayText(key: string) { return TranslateDirective.getTranslation(key, true); }
    crremDisplay = {projection: true, crossing: true, target: true};
    @HostListener('document:keydown.escape') closeExpandedCompass() { this.expanded = false; }
    values: CompassRecord[] = [];
    @Input() showInReport: boolean;

    measurements: DataType[] = [
        DataType.electricityConsumption,
        DataType.electricityConsumptionOffPeak
    ];

    @Input()
    set location(location: Location) {
        this._location = location;
        if (location && location?.info?.buildingType) {
            this.sendQuery('getBuildingType', location.info.buildingType)
                .subscribe(b => {
                    this.buildingType = b;
                });
        }
        this.recomputeValues();
    }

    @Input()
    set meters(meters: Entity[]) {
        this._meters = meters;
        this.recomputeValues();
    }

    ngOnInit() {
        this.subscribeTo('getEnergyCompassMeasurements')
            .subscribe((d: ChartDataPerMeasurement) => {
                this.latestMeasurements = d;
                this.recomputeValues();
            });
        this.weiiCalculationService.options$.subscribe(() => this.recomputeValues());
    }

    protected recomputeValues() {
        if (!this.latestMeasurements || !this.computeValues) {
            this.values = [];
            return;
        }
        const currentYear = moment().year();
        const computed = this.computeValues(this.latestMeasurements)
            ?.filter(value => value.year <= currentYear)
            .map(value => ({...value, provisional: value.provisional ?? value.year === currentYear}));
        this.values = computed || [];
    }

    computeValues: (d: ChartDataPerMeasurement) => CompassRecord[] = null;

    get location(): Location | null {
        return this._location;
    }

    get meters(): Entity[] | null {
        return this._meters;
    }

    get parisProofValue(): number | null {
        return null;
    }

    get noDataText(): string {
        return '';
    }

    get applyEvCorrection(): boolean {
        return this.weiiCalculationService.isOptionEnabled(WeiiCalculationOptionId.ExcludeEvCharging)
            && this.evCorrectionAvailable;
    }

    get latestCalculation(): WeiiCalculationResult | null {
        return this.values.length > 0 ? this.values[this.values.length - 1].calculation || null : null;
    }

    get evCorrectionAvailable(): boolean {
        return this.values.some(value => value.calculation?.evCorrectionAvailable === true);
    }

    get calculationOptionAvailability(): WeiiCalculationOptionAvailability {
        return {
            [WeiiCalculationOptionId.ExcludeEvCharging]: this.evCorrectionAvailable,
            [WeiiCalculationOptionId.IncludeOnsiteGeneration]: this.values.some(
                value => value.calculation?.onsiteGenerationAvailable === true),
            [WeiiCalculationOptionId.NormalizeClimateYear]: !this.isFullElectric
                && this.values.filter(value => !value.provisional)
                    .some(value => value.calculation?.climateNormalizationAvailable === true)
        };
    }

    get isFullElectric(): boolean {
        return this.location?.info?.allElectric === true;
    }

    setApplyEvCorrection(value: boolean): void {
        if (!value || this.evCorrectionAvailable) {
            this.weiiCalculationService.setOptionEnabled(WeiiCalculationOptionId.ExcludeEvCharging, value);
        }
    }

    get chartMaximum(): number {
        return Math.max(...this.values.map(value => value.value), this.parisProofValue ?? 0, 1) * 1.18;
    }

    getValueHeight(value: number): number {
        return Math.max(4, Math.min(100, value / this.chartMaximum * 100));
    }

    get parisProofLinePosition(): number {
        return Math.max(0, Math.min(100, (this.parisProofValue ?? 0) / this.chartMaximum * 100));
    }

    isParisProof(value: number): boolean {
        return this.parisProofValue != null && value <= this.parisProofValue;
    }
}

export interface CompassRecord {
    year: number;
    value: number;
    calculation?: WeiiCalculationResult;
    provisional?: boolean;
    provisionalThrough?: string;
}
